import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select2 from "../../Components/Accounting/Select2";
import AppModal from "../../Components/Accounting/AppModal";
import ajaxAccounting, { ajaxItems } from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import { canAddRow, addRowBlockedReason } from "../../util/accounting/formRows";
import toast, { Toaster } from "react-hot-toast";

/** Chosen from the item dropdown to open the "new item" form. */
const NEW_ITEM = "__new_item__";

const emptyLine = () => ({
  item_id: "", description: "", quantity: 1, unit_price: "",
});

const NewInvoice = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceType = searchParams.get("type") || "outgoing";
  const isOutgoing = invoiceType === "outgoing";

  const [financialYears, setFinancialYears] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  // One account for the whole invoice, and VAT and discount agreed on the whole
  // invoice rather than line by line — which is how they are actually agreed.
  const [form, setForm] = useState({
    contact_id: "", invoice_date: new Date().toISOString().split("T")[0],
    due_date: "", reference: "", notes: "", currency_id: "", is_credit_note: 0,
    invoice_account_id: "", tax_rate_id: "", discount_type: "percentage", discount_value: "",
  });
  const [controlAccount, setControlAccount] = useState(null);

  // Adding a catalogue item without leaving the invoice being written.
  const [allAccounts, setAllAccounts] = useState([]);
  const [newItemFor, setNewItemFor] = useState(null); // which line asked for it
  const [itemForm, setItemForm] = useState({
    code:"", name:"", category:"service", default_price:"",
    default_account_id:"", inventory_account_id:"", cogs_account_id:"", cost_price:"",
  });
  const [savingItem, setSavingItem] = useState(false);
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFinancialYears();
    fetchContacts();
    fetchAccounts();
    fetchTaxRates();
    fetchCatalogItems();
    fetchControlAccount();
    setLines([emptyLine()]);
  }, [invoiceType]); // eslint-disable-line

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      setFinancialYears(res.details || []);
      const cur = (res.details || []).find((y) => y.is_current === 1);
      setSelectedFY(String(cur?.id || res.details?.[0]?.id || ""));
    }
  };

  const fetchContacts = async () => {
    const contactType = isOutgoing ? "customer" : "supplier";
    const res = await ajaxAccounting.listContacts({ type: contactType });
    if (res.status === "OK") setContacts(res.details || []);
  };

  /**
   * The other side of the entry is never a choice: a sales invoice debits
   * Accounts Receivable, a purchase invoice credits Accounts Payable. It is
   * shown so the bookkeeping is visible, but it is not editable.
   */
  const fetchControlAccount = async () => {
    const [cfg, accts] = await Promise.all([
      ajaxAccounting.listSystemConfig(),
      ajaxAccounting.listAccounts({}),
    ]);
    if (cfg.status !== "OK" || accts.status !== "OK") return;
    setAllAccounts(accts.details || []);

    const key = isOutgoing ? "default_accounts_receivable" : "default_accounts_payable";
    const id = (cfg.details || []).find((c) => c.config_key === key)?.config_value;
    const account = (accts.details || []).find((a) => String(a.id) === String(id));
    setControlAccount(account || null);
  };

  // Sales invoice lines post to income accounts. Purchase invoice lines usually post to
  // expense accounts, but a trackable purchase item (raw materials / stock) posts to an
  // asset/inventory account instead — so incoming invoices need both lists, or a stock
  // item's auto-filled account never shows up as a valid selection.
  // (This used to request type "revenue" for outgoing, which doesn't exist in the chart of
  // accounts — the real value is "income" — so the picker was always empty for every sale.)
  const fetchAccounts = async () => {
    if (isOutgoing) {
      const res = await ajaxAccounting.listAccounts({ type: "income", postable_only: 1 });
      if (res.status === "OK") setAccounts(res.details || []);
    } else {
      const [exp, ast] = await Promise.all([
        ajaxAccounting.listAccounts({ type: "expense", postable_only: 1 }),
        ajaxAccounting.listAccounts({ type: "asset", postable_only: 1 }),
      ]);
      const combined = [...(exp.status === "OK" ? exp.details || [] : []), ...(ast.status === "OK" ? ast.details || [] : [])];
      setAccounts(combined);
    }
  };

  const fetchTaxRates = async () => {
    const res = await ajaxAccounting.listTaxes();
    if (res.status === "OK") setTaxRates(res.details || []);
  };

  const fetchCatalogItems = async () => {
    const res = isOutgoing ? await ajaxItems.listSalesItems({}) : await ajaxItems.listPurchaseItems({});
    setCatalogItems(res.status === "OK" && Array.isArray(res.details) ? res.details : []);
  };

  const setLine = (i, field, val) => {
    const updated = [...lines];
    updated[i] = { ...updated[i], [field]: val };
    setLines(updated);
  };

  const applyItemToLine = (i, itemId) => {
    if (itemId === NEW_ITEM) { openNewItem(i); return; }
    const item = catalogItems.find((it) => String(it.id) === String(itemId));
    if (!item) { setLine(i, "item_id", ""); return; }
    const updated = [...lines];
    updated[i] = {
      ...updated[i],
      item_id: String(item.id),
      description: updated[i].description || item.name,
      unit_price: item.default_price,
    };
    setLines(updated);

    // The account and VAT now belong to the invoice, so an item's defaults seed
    // them only while they are still empty — the first item picked should not
    // be overruled by the second.
    setForm((f) => ({
      ...f,
      invoice_account_id: f.invoice_account_id || String(item.default_account_id || ""),
      tax_rate_id: f.tax_rate_id || (item.default_tax_rate_id ? String(item.default_tax_rate_id) : ""),
    }));
  };

  /**
   * Creates a catalogue item from inside the invoice and drops it straight onto
   * the line that asked for it, so writing an invoice for something new does not
   * mean abandoning the invoice to go and set the item up first.
   */
  const openNewItem = (lineIndex) => {
    setItemForm({
      code: "", name: "", category: isOutgoing ? "service" : "expense",
      default_price: "", cost_price: "",
      default_account_id: form.invoice_account_id || "",
      inventory_account_id: "", cogs_account_id: "",
    });
    setNewItemFor(lineIndex);
  };

  const saveNewItem = async (e) => {
    e.preventDefault();
    if (!itemForm.code || !itemForm.name || !itemForm.default_account_id) {
      toast.error("Code, name and account are required"); return;
    }
    if (itemForm.category === "product" && isOutgoing &&
        (!itemForm.inventory_account_id || !itemForm.cogs_account_id)) {
      toast.error("A stocked product needs an inventory and a cost-of-sales account");
      return;
    }

    setSavingItem(true);
    const payload = {
      code: itemForm.code,
      name: itemForm.name,
      category: itemForm.category,
      default_account_id: parseInt(itemForm.default_account_id),
      default_price: parseFloat(itemForm.default_price) || 0,
      default_tax_rate_id: form.tax_rate_id ? parseInt(form.tax_rate_id) : null,
      ...(itemForm.category === "product"
        ? {
            cost_price: parseFloat(itemForm.cost_price) || 0,
            inventory_account_id: itemForm.inventory_account_id ? parseInt(itemForm.inventory_account_id) : null,
            cogs_account_id: itemForm.cogs_account_id ? parseInt(itemForm.cogs_account_id) : null,
          }
        : {}),
    };
    const res = isOutgoing
      ? await ajaxItems.createSalesItem(payload)
      : await ajaxItems.createPurchaseItem(payload);
    setSavingItem(false);

    if (res.status !== "OK") { toast.error(res.message || "Could not save the item"); return; }

    toast.success(`${itemForm.name} added to the catalogue`);
    const line = newItemFor;
    setNewItemFor(null);

    // Reload the catalogue, then select the new item on the line that asked.
    const list = isOutgoing ? await ajaxItems.listSalesItems({}) : await ajaxItems.listPurchaseItems({});
    const items = list.status === "OK" && Array.isArray(list.details) ? list.details : [];
    setCatalogItems(items);

    const created = items.find((it) => String(it.id) === String(res.details?.id));
    if (created && line !== null) {
      setLines((prev) => prev.map((l, i) => i === line ? {
        ...l,
        item_id: String(created.id),
        description: l.description || created.name,
        unit_price: created.default_price,
      } : l));
      setForm((f) => ({
        ...f,
        invoice_account_id: f.invoice_account_id || String(created.default_account_id || ""),
      }));
    }
  };

  const taxRateFor = (id) => taxRates.find((t) => String(t.id) === String(id));

  const lineGross = (line) => (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);

  // Discount and VAT are agreed for the invoice, so they are worked out on the
  // invoice total. The backend then shares them across the lines, which is what
  // keeps the stored lines and the journal in step.
  const totals = (() => {
    const gross = lines.reduce((sum, l) => sum + lineGross(l), 0);

    const value = parseFloat(form.discount_value) || 0;
    let discount = 0;
    if (value > 0) {
      discount = form.discount_type === "percentage"
        ? gross * (Math.min(100, value) / 100)
        : Math.min(value, gross);
    }

    let subtotal = gross - discount;
    const tax = taxRateFor(form.tax_rate_id);
    const rate = tax ? parseFloat(tax.rate) : 0;
    let taxAmt = 0;
    if (rate > 0) {
      if (tax.type === "exclusive") {
        taxAmt = subtotal * (rate / 100);
      } else {
        taxAmt = subtotal - subtotal / (1 + rate / 100);
        subtotal -= taxAmt;
      }
    }
    return { gross, subtotal, discount, tax: taxAmt };
  })();
  const grandTotal = totals.subtotal + totals.tax;

  const LINE_REQUIRED = [
    { key: "description", label: "a description" },
    { key: "unit_price", label: "a unit price", numeric: true },
  ];
  const canAddLine = canAddRow(lines, LINE_REQUIRED);
  const addLineBlocked = addRowBlockedReason(lines, LINE_REQUIRED);

  // Offered inside the dropdown itself — that is where someone looks when what
  // they want to bill for is not in the list.
  const catalogOptions = [
    { value: NEW_ITEM, label: `+ Add a new ${isOutgoing ? "product or service" : "purchase item"}…` },
    ...catalogItems.map((it) => ({ value: String(it.id), label: `${it.code} — ${it.name}` })),
  ];
  const accountOptions = accounts.map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }));
  const taxOptions = [{ value: "", label: "No tax" }, ...taxRates.map((t) => ({ value: String(t.id), label: `${t.name} (${t.rate}%)` }))];

  const handleSave = async (andApprove = false) => {
    if (!form.contact_id) { toast.error("Please select a contact."); return; }
    if (!selectedFY) { toast.error("Please select a financial year."); return; }
    if (!form.due_date) { toast.error("Due date is required."); return; }
    if (!form.invoice_account_id) {
      toast.error(`Please choose the ${isOutgoing ? "income" : "expense"} account this invoice belongs to.`);
      return;
    }
    const validLines = lines.filter((l) => (parseFloat(l.unit_price) || 0) > 0);
    if (validLines.length === 0) { toast.error("Add at least one line item."); return; }

    setSaving(true);
    const payload = {
      type: invoiceType,
      contact_id: parseInt(form.contact_id),
      contact_type: isOutgoing ? "customer" : "supplier",
      invoice_date: form.invoice_date,
      due_date: form.due_date,
      reference: form.reference,
      notes: form.notes,
      financial_year_id: parseInt(selectedFY),
      is_credit_note: form.is_credit_note,
      // Agreed once for the invoice; the backend shares them across the lines.
      invoice_tax_rate_id: form.tax_rate_id ? parseInt(form.tax_rate_id) : null,
      ...(parseFloat(form.discount_value) > 0
        ? { invoice_discount: { type: form.discount_type, value: parseFloat(form.discount_value) } }
        : {}),
      lines: validLines.map((l) => ({
        account_id: parseInt(form.invoice_account_id),
        description: l.description,
        quantity: parseFloat(l.quantity) || 1,
        unit_price: parseFloat(l.unit_price),
        ...(isOutgoing
          ? { sales_item_id: l.item_id ? parseInt(l.item_id) : null }
          : { purchase_item_id: l.item_id ? parseInt(l.item_id) : null }),
      })),
    };

    const res = await ajaxAccounting.createInvoice(payload);
    if (res.status === "OK") {
      if (andApprove) {
        const appRes = await ajaxAccounting.approveInvoice({ id: res.details?.id });
        if (appRes.status === "OK") toast.success("Invoice created and approved!");
        else toast.error(appRes.message || "Invoice created, but approval failed (e.g. insufficient stock). Approve manually once resolved.");
      } else {
        toast.success("Invoice saved as draft!");
      }
      navigate(`/accounting/invoices?type=${invoiceType}`);
    } else {
      toast.error(res.message || "Failed to save invoice.");
    }
    setSaving(false);
  };

  return (
    <AppContainer
      title={isOutgoing ? "New Sales Invoice" : "New Purchase Invoice"}
      previousPageTitle="Invoices"
      description={isOutgoing ? "Create a customer sales invoice. Once approved, it posts to AR and income accounts automatically." : "Record a supplier purchase invoice. Once approved, it posts to AP and expense accounts automatically."}
      actions={[
        { label: "Save Draft", icon: "fa-solid fa-floppy-disk", className: "btn btn-outline-secondary", onClick: () => handleSave(false), disabled: saving },
        { label: "Save & Approve", icon: "fa-solid fa-check", className: "btn btn-primary", onClick: () => handleSave(true), disabled: saving },
        { label: "Cancel", icon: "fa-solid fa-xmark", className: "btn btn-light", onClick: () => navigate(`/accounting/invoices?type=${invoiceType}`) },
      ]}
    >
      <Toaster />
      <div className="row">
        <div className="col-lg-9">
          <div className="card custom-card p-4 mb-3">
            <h6 className="fw-semibold mb-3">{isOutgoing ? "Customer" : "Supplier"} Details</h6>
            <div className="row g-3">
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">{isOutgoing ? "Customer" : "Supplier"} <span className="tx-danger">*</span></label>
                <Select2 options={contacts.map((c) => ({ value: String(c.id), label: c.name }))} value={form.contact_id} onChange={(v) => setForm({ ...form, contact_id: v })} placeholder={`Select ${isOutgoing ? "Customer" : "Supplier"}`} />
                <Link to="/accounting/contacts" className="tx-12 tx-primary mt-1 d-inline-block">+ Add new contact</Link>
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">Financial Year <span className="tx-danger">*</span></label>
                <Select2 options={financialYears.map((y) => ({ value: String(y.id), label: y.name }))} value={selectedFY} onChange={setSelectedFY} placeholder="Select Year" />
              </div>
              <div className="col-lg-4">
                <label className="form-label tx-12 fw-semibold">Invoice Date</label>
                <input type="date" className="form-control" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
              </div>
              <div className="col-lg-4">
                <label className="form-label tx-12 fw-semibold">Due Date <span className="tx-danger">*</span></label>
                <input type="date" className="form-control" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div className="col-lg-4">
                <label className="form-label tx-12 fw-semibold">Reference</label>
                <input type="text" className="form-control" placeholder="PO / Ref number" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </div>
              {/* Both sides of the entry, so the bookkeeping is visible. Only
                  one of them is a decision. */}
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">
                  {isOutgoing ? "Income account" : "Expense account"} <span className="tx-danger">*</span>
                </label>
                <Select2
                  options={accountOptions}
                  value={form.invoice_account_id}
                  onChange={(v) => setForm({ ...form, invoice_account_id: v })}
                  placeholder={`Select the ${isOutgoing ? "income" : "expense"} account`}
                />
                <small className="tx-muted tx-11">Every line on this invoice posts here.</small>
              </div>
              <div className="col-lg-6">
                <label className="form-label tx-12 fw-semibold">
                  {isOutgoing ? "Debit (receivable)" : "Credit (payable)"}
                </label>
                <Select2
                  options={controlAccount
                    ? [{ value: String(controlAccount.id), label: `${controlAccount.account_code} — ${controlAccount.name}` }]
                    : []}
                  value={controlAccount ? String(controlAccount.id) : ""}
                  onChange={() => {}}
                  disabled
                  placeholder={controlAccount ? "" : "Not configured"}
                />
                <small className="tx-muted tx-11">
                  {controlAccount
                    ? `Set once in System Configuration — every ${isOutgoing ? "sales" : "purchase"} invoice uses it.`
                    : `No default ${isOutgoing ? "receivable" : "payable"} account is configured.`}
                </small>
              </div>

              <div className="col-lg-4">
                <label className="form-label tx-12 fw-semibold">VAT (whole invoice)</label>
                <Select2 options={taxOptions} value={form.tax_rate_id} onChange={(v) => setForm({ ...form, tax_rate_id: v })} placeholder="No tax" />
              </div>
              <div className="col-lg-4">
                <label className="form-label tx-12 fw-semibold">Discount</label>
                <div className="input-group">
                  <select
                    className="form-select"
                    style={{ maxWidth: "110px" }}
                    value={form.discount_type}
                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    aria-label="Discount type"
                  >
                    <option value="percentage">%</option>
                    <option value="amount">Amount</option>
                  </select>
                  <input
                    type="number" min="0" step="0.01" className="form-control"
                    placeholder={form.discount_type === "percentage" ? "e.g. 10" : "e.g. 50000"}
                    value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  />
                </div>
                <small className="tx-muted tx-11">Applied to the invoice, then shared across its lines.</small>
              </div>

              <div className="col-12">
                <label className="form-label tx-12 fw-semibold">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="col-lg-4">
                <div className="form-check mt-2">
                  <input className="form-check-input" type="checkbox" checked={form.is_credit_note === 1} onChange={(e) => setForm({ ...form, is_credit_note: e.target.checked ? 1 : 0 })} id="creditNote" />
                  <label className="form-check-label tx-13" htmlFor="creditNote">This is a Credit Note</label>
                </div>
              </div>
            </div>
          </div>

          <div className="card custom-card p-4">
            <div className="d-flex justify-content-between mb-3">
              <h6 className="fw-semibold mb-0">Line Items</h6>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setLines([...lines, emptyLine()])}
                disabled={!canAddLine}
                title={addLineBlocked || "Add another line"}
              >
                <i className="fa-solid fa-plus me-1"></i>Add Line
              </button>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered" style={{ fontSize: "0.84rem" }}>
                <thead className="bg-light">
                  <tr>
                    <th style={{ minWidth: "180px" }}>{isOutgoing ? "Product / Service" : "Purchase Item"}</th>
                    <th>Description</th>
                    <th style={{ width: "70px" }}>Qty</th>
                    <th style={{ width: "120px" }}>Unit Price</th>
                    <th style={{ width: "130px" }}>Total</th>
                    <th style={{ width: "40px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => {
                    const item = catalogItems.find((it) => String(it.id) === String(line.item_id));
                    const stockShort = isOutgoing && item && item.category === "product" && (parseFloat(line.quantity) || 0) > item.quantity_on_hand;
                    return (
                      <tr key={i}>
                        <td>
                          <Select2 options={catalogOptions} value={line.item_id} onChange={(v) => applyItemToLine(i, v)} placeholder="Optional — pick a saved item" />
                          {isOutgoing && item && item.category === "product" && (
                            <div className={`tx-11 mt-1 ${stockShort ? "tx-danger fw-bold" : "tx-muted"}`}>In stock: {item.quantity_on_hand}</div>
                          )}
                          {!isOutgoing && item && item.track_inventory && (
                            <div className="tx-11 mt-1 tx-muted">Current stock: {item.quantity_on_hand}</div>
                          )}
                        </td>
                        <td><input type="text" className="form-control form-control-sm" placeholder="Description" value={line.description} onChange={(e) => setLine(i, "description", e.target.value)} /></td>
                        <td><input type="number" className="form-control form-control-sm" min="1" value={line.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} /></td>
                        <td><input type="number" className="form-control form-control-sm" min="0" step="0.01" placeholder="0.00" value={line.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} /></td>
                        <td className="fw-bold text-end">{functions.formatCurrency(lineGross(line))}</td>
                        <td>{lines.length > 1 && <button className="btn btn-sm btn-light tx-danger" onClick={() => setLines(lines.filter((_, idx) => idx !== i))}><i className="fa-solid fa-trash"></i></button>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-3">
          <div className="card custom-card p-3 mb-3">
            <h6 className="fw-semibold mb-3">Invoice Summary</h6>
            <div className="d-flex justify-content-between mb-2 tx-13">
              <span className="tx-muted">Subtotal</span>
              <span>{functions.formatCurrency(totals.gross)}</span>
            </div>
            {totals.discount > 0 && (
              <>
                <div className="d-flex justify-content-between mb-2 tx-13">
                  <span className="tx-muted">
                    Discount on invoice
                    {form.discount_type === "percentage" && parseFloat(form.discount_value) > 0 && (
                      <span className="tx-11"> ({parseFloat(form.discount_value)}%)</span>
                    )}
                  </span>
                  <span className="tx-danger">-{functions.formatCurrency(totals.discount)}</span>
                </div>
                {/* The figure VAT is actually charged on, so the order is visible. */}
                <div className="d-flex justify-content-between mb-2 tx-13">
                  <span className="tx-muted">After discount</span>
                  <span>{functions.formatCurrency(totals.gross - totals.discount)}</span>
                </div>
              </>
            )}
            <div className="d-flex justify-content-between mb-2 tx-13">
              <span className="tx-muted">
                Tax
                {taxRateFor(form.tax_rate_id) && (
                  <span className="tx-11"> ({taxRateFor(form.tax_rate_id).rate}%{totals.discount > 0 ? " of the discounted amount" : ""})</span>
                )}
              </span>
              <span>{functions.formatCurrency(totals.tax)}</span>
            </div>
            <hr />
            <div className="d-flex justify-content-between fw-bold">
              <span>Grand Total</span>
              <span className="tx-primary" style={{ fontSize: "1.15rem" }}>{functions.formatCurrency(grandTotal)}</span>
            </div>
          </div>
          <div className="card custom-card p-3">
            <h6 className="fw-semibold mb-2">Flow</h6>
            <div className="tx-13 tx-muted">
              <div className="mb-1">1. Save Draft → Review</div>
              <div className="mb-1">2. Approve → Posts to ledger{isOutgoing ? " (and moves stock for products)" : ""}</div>
              <div className="mb-1">3. Record Payment → Updates balance</div>
              <div>4. Fully paid → Status: Paid</div>
            </div>
          </div>
        </div>
      </div>

      {newItemFor !== null && (
        <AppModal
          title={`New ${isOutgoing ? "product or service" : "purchase item"}`}
          icon="fa-solid fa-plus"
          subtitle="Saved to the catalogue and added to this line."
          onClose={() => setNewItemFor(null)}
          onSubmit={saveNewItem}
          busy={savingItem}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setNewItemFor(null)} disabled={savingItem}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={savingItem}>
                {savingItem ? "Saving..." : "Save & use"}
              </button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Code *</label>
              <input className="form-control text-uppercase" placeholder="e.g. CONSULT" value={itemForm.code}
                onChange={(e) => setItemForm({ ...itemForm, code: e.target.value })} required />
            </div>
            <div className="col-lg-8">
              <label className="form-label tx-12 fw-semibold">Name *</label>
              <input className="form-control" placeholder="e.g. Consulting day rate" value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} required />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Type</label>
              <select className="form-select" value={itemForm.category}
                onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}>
                <option value="service">Service</option>
                <option value="product">Product (stocked)</option>
                {!isOutgoing && <option value="expense">Expense</option>}
              </select>
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">{isOutgoing ? "Selling price" : "Cost"}</label>
              <input type="number" min="0" step="0.01" className="form-control" placeholder="0.00"
                value={itemForm.default_price}
                onChange={(e) => setItemForm({ ...itemForm, default_price: e.target.value })} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">{isOutgoing ? "Income" : "Expense"} account *</label>
              <Select2 options={accountOptions} value={itemForm.default_account_id}
                onChange={(v) => setItemForm({ ...itemForm, default_account_id: v })}
                placeholder="Select account" />
            </div>

            {itemForm.category === "product" && (
              <>
                <div className="col-lg-4">
                  <label className="form-label tx-12 fw-semibold">Cost price</label>
                  <input type="number" min="0" step="0.01" className="form-control" placeholder="0.00"
                    value={itemForm.cost_price}
                    onChange={(e) => setItemForm({ ...itemForm, cost_price: e.target.value })} />
                </div>
                <div className="col-lg-4">
                  <label className="form-label tx-12 fw-semibold">Inventory account</label>
                  <Select2
                    options={allAccounts.filter((a) => a.type === "asset")
                      .map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }))}
                    value={itemForm.inventory_account_id}
                    onChange={(v) => setItemForm({ ...itemForm, inventory_account_id: v })}
                    placeholder="Where stock is held" />
                </div>
                <div className="col-lg-4">
                  <label className="form-label tx-12 fw-semibold">Cost of sales account</label>
                  <Select2
                    options={allAccounts.filter((a) => a.type === "expense")
                      .map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }))}
                    value={itemForm.cogs_account_id}
                    onChange={(v) => setItemForm({ ...itemForm, cogs_account_id: v })}
                    placeholder="Charged when it sells" />
                </div>
                <div className="col-12">
                  <div className="tx-11 tx-muted">
                    A stocked product moves stock when the invoice is approved, so it needs
                    somewhere to hold the stock and somewhere to charge its cost.
                  </div>
                </div>
              </>
            )}
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default NewInvoice;
